import { type AttributeFilter, type LocationArea } from '@kijiji/generated/graphql-types'
import { type TFunction } from 'next-i18next'

import { FILTER_CANONICAL } from '@/constants/search'
import { getAttributeFilterDescription } from '@/domain/srp/filters/getAttributeFilterDescription'
import { RANGE_FILTER_LABEL_OVERRIDE } from '@/features/filters/constants/rangeFilterLabelOverrides'
import { type RangeFilterMinMax, isAttributeFilter, isRangeFilterWithMinMax } from '@/types/search'
import { sendToLogger } from '@/utils/sendToLogger'

export type QuickFilterLabelConfig = {
  [filterName: string]: {
    labelOverride: (
      filter: AttributeFilter | RangeFilterMinMax,
      t: TFunction,
      area?: LocationArea | null
    ) => string | undefined
  }
}

const commonErrorContext = {
  fingerprint: ['QUICK_FILTER_LABEL_CONFIG'],
}

export const QUICK_FILTER_LABEL_CONFIG: QuickFilterLabelConfig = {
  [FILTER_CANONICAL.LOCATION]: {
    labelOverride: (filter, t, area) => {
      if (isAttributeFilter(filter)) {
        if (area) {
          return t('filters.location.radial_location', { radius: area.radius })
        }
        return getAttributeFilterDescription(filter)
      }
      sendToLogger(
        new Error(`Invalid filter type for ${FILTER_CANONICAL.LOCATION}`),
        commonErrorContext
      )
    },
  },
  [FILTER_CANONICAL.CATEGORY]: {
    labelOverride: (filter) => {
      if (isAttributeFilter(filter)) {
        return getAttributeFilterDescription(filter)
      }
      sendToLogger(
        new Error(`Invalid filter type for ${FILTER_CANONICAL.CATEGORY}`),
        commonErrorContext
      )
    },
  },
  [FILTER_CANONICAL.PRICE_RANGE]: {
    labelOverride: (filter, t) => {
      if (isRangeFilterWithMinMax(filter)) {
        const { selectedRangeValues, label, name } = filter
        if (!selectedRangeValues) return label
        const formattedValues = RANGE_FILTER_LABEL_OVERRIDE.FORMAT_PRICE_SHORTHAND(filter)

        return RANGE_FILTER_LABEL_OVERRIDE.FORMAT_RANGE(name, formattedValues, t)
      }
      sendToLogger(
        new Error(`Invalid filter type for ${FILTER_CANONICAL.PRICE_RANGE}`),
        commonErrorContext
      )
    },
  },
  [FILTER_CANONICAL.CARYEAR_RANGE]: {
    labelOverride: (filter, t) => {
      if (isRangeFilterWithMinMax(filter)) {
        const { selectedRangeValues, label, name } = filter
        const formattedRangeValues = {
          min: selectedRangeValues?.min ? selectedRangeValues.min.toString() : null,
          max: selectedRangeValues?.max ? selectedRangeValues.max.toString() : null,
        }
        return selectedRangeValues
          ? RANGE_FILTER_LABEL_OVERRIDE.FORMAT_RANGE(name, formattedRangeValues, t)
          : label
      }
      sendToLogger(
        new Error(`Invalid filter type for ${FILTER_CANONICAL.CARYEAR_RANGE}`),
        commonErrorContext
      )
    },
  },
  [FILTER_CANONICAL.MILEAGE_RANGE]: {
    labelOverride: (filter, t) => {
      if (isRangeFilterWithMinMax(filter)) {
        const { selectedRangeValues, label, name } = filter
        if (!selectedRangeValues) return label

        const formattedValues = RANGE_FILTER_LABEL_OVERRIDE.FORMAT_NUMBER(filter)

        return `${RANGE_FILTER_LABEL_OVERRIDE.FORMAT_RANGE(name, formattedValues, t)} km`
      }
      sendToLogger(
        new Error(`Invalid filter type for ${FILTER_CANONICAL.MILEAGE_RANGE}`),
        commonErrorContext
      )
    },
  },
}
